import React, {useReducer} from 'react'
import gql from 'graphql-tag'
import {Mutation} from 'react-apollo'
import Layout from 'components/shared/Layout'
import QueryParams from 'components/shared/QueryParams'
import Query from 'components/shared/Query'
import OrderSteps from 'components/shared/OrderSteps'
import {webOrdersQuery} from 'components/PrivateRoutes/MyEstates/WebOrders'
import StepRenderer from '../StepRenderer'
import {getOrderingSteps, recursivelySetNextStep} from '../helpers'
import {stateFromWebOrder, webOrderInputFromState} from './helpers'

const webOrderFragment = gql`
  fragment NewEstateWebOrder on WebOrder {
    id
    customerRef
    m2
    floors
    floorNumber
    cadastreAddressId
    broker {
      id
      name
      email
    }
    customer {
      id
    }
    segment {
      id
      type
      iconUrl
    }
    address {
      id
      street
      postalCode
      postalArea
      knr
      gnr
      bnr
      location {
        lat
        lon
      }
    }
    estateSellers {
      id
      type
      name
      email
      phone
    }
    reservedBooking {
      id
      startsAt
      serviceProvider {
        id
        name
      }
    }
    thirdPartyApplicationName
  }
`

const deleteReservationMutation = gql`
  mutation UpdateWebOrder($input: WebOrderInput!) {
    UpdateWebOrder(input: $input) {
      webOrder {
        id
        reservedBooking {
          id
          startsAt
          endsAt
          expiresAt
          serviceProvider {
            id
            name
          }
        }
      }
    }
  }
`

const webOrderQuery = gql`
  query WebOrderQuery($id: ID!) {
    webOrder(id: $id) {
      ...NewEstateWebOrder
    }
  }
  ${webOrderFragment}
`

const createWebOrderMutation = gql`
  mutation CreateWebOrder($input: WebOrderInput!) {
    CreateWebOrder(input: $input) {
      webOrder {
        ...NewEstateWebOrder
      }
    }
  }
  ${webOrderFragment}
`

const updateWebOrderMutation = gql`
  mutation UpdateWebOrder($input: WebOrderInput!) {
    UpdateWebOrder(input: $input) {
      webOrder {
        ...NewEstateWebOrder
      }
    }
  }
  ${webOrderFragment}
`

const NewEstate = (props) => {
  const reducer = (state, action) => {
    switch (action.type) {
      case 'setAddress': {
        return {...state, address: action.address}
      }
      case 'setSegment': {
        return {...state, segment: action.segment}
      }
      case 'setInformation': {
        return {...state, information: action.information}
      }
      default: {
        throw new Error(`Unknown action ${action}`)
      }
    }
  }

  const [estate, estateReducer] = useReducer(reducer, stateFromWebOrder(props.webOrder))

  const submitWebOrder = () => {
    const webOrderInput = webOrderInputFromState(estate)
    if (props.webOrder) {
      return props.updateWebOrderMutation({
        variables: {
          input: {
            id: props.webOrder.id,
            ...webOrderInput,
          }
        }
      }).then(({data}) => data.UpdateWebOrder.webOrder)
    }
    return props.createWebOrderMutation({
      variables: {
        input: webOrderInput,
      }
    }).then(({data}) => data.CreateWebOrder.webOrder)
  }

  const allSteps = getOrderingSteps({webOrder: props.webOrder, estate})
  const validSteps = []
  recursivelySetNextStep(validSteps, allSteps.filter(s => s.scope === 'estate'))
  recursivelySetNextStep(validSteps, allSteps.filter(s => s.scope === 'order'))

  return (
    <Layout documentTitle="Ny bestilling">
      <OrderSteps
        webOrder={props.webOrder}
        steps={validSteps}
        currentStep={props.queryParams.step || 'identifiser-eiendom'}
      />

      <Mutation mutation={deleteReservationMutation}>
        {(deleteReservationMutation, {loading}) => (
          <StepRenderer
            {...props}
            defaultStep="identifiser-eiendom"
            scope="estate"
            removeReservation={() => {
              deleteReservationMutation({
                variables: {
                  input: {
                    id: props.webOrder.id,
                    reservedBookingId: ''
                  }
                }
              })
            }}
            isRemovingReservation={loading}
            estate={estate}
            estateReducer={estateReducer}
            submitWebOrder={submitWebOrder}
          />
        )}
      </Mutation>
    </Layout>
  )
}

const EstateWithActions = (props) => {
  return (
    <QueryParams>
      {({queryParams, setQueryParams}) => (
        <Mutation mutation={updateWebOrderMutation}>
          {(updateWebOrder) => (
            <Mutation
              mutation={createWebOrderMutation}
              refetchQueries={[{query: webOrdersQuery}]}
            >
              {(createWebOrder) => (
                <NewEstate
                  {...props}
                  queryParams={queryParams}
                  setQueryParams={setQueryParams}
                  createWebOrderMutation={createWebOrder}
                  updateWebOrderMutation={updateWebOrder}
                />
              )}
            </Mutation>
          )}
        </Mutation>
      )}
    </QueryParams>
  )
}

const WithWebOrderData = ({match}) => {
  if (match.params.webOrderId) {
    return (
      <Query
        inlineLoading
        query={webOrderQuery}
        variables={{id: match.params.webOrderId}}
      >
        {({getData}) => {
          const webOrder = getData('webOrder')
          return <EstateWithActions webOrder={webOrder} />
        }}
      </Query>
    )
  }

  return <EstateWithActions />
}

export default WithWebOrderData
